import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { Plus, FolderKanban, Edit2, Users } from "lucide-react";

const ProjectsPage = () => {
  const { t } = useTranslation('operations');
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    client_name: "",
    sector: "life-sciences",
    assigned_auditors: []
  });

  const fetchData = async () => {
    try {
      const [projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data.filter(u => u.role === "auditor"));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, formData, { withCredentials: true });
      setShowModal(false);
      setFormData({ name: "", client_name: "", sector: "life-sciences", assigned_auditors: [] });
      fetchData();
    } catch (error) {
      console.error("Error creating project:", error);
    }
  };

  const handleStatusUpdate = async (projectId, newStatus) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { status: newStatus }, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Error updating project:", error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: "badge-green",
      "on-hold": "badge-amber",
      completed: "badge-blue"
    };
    return badges[status] || "badge-gray";
  };

  const sectorColors = {
    "life-sciences": "#4ADE80",
    "aerospace": "#FBBF24",
    "industrial": "#F87171",
    "iot": "#E8A838"
  };

  return (
    <QMSLayout title={t('projects.title')}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <p className="text-[#8B95A5]">{t('projects.manageProjects')}</p>
        <button onClick={() => setShowModal(true)} className="btn-primary" data-testid="create-project-btn">
          <Plus size={18} /> {t('projects.newProject')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="project-stats">
        <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
          <p className="kpi-label">{t('projects.totalProjects')}</p>
          <p className="kpi-value">{projects.length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#4ADE80" }}>
          <p className="kpi-label">{t('projects.active')}</p>
          <p className="kpi-value">{projects.filter(p => p.status === "active").length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#FBBF24" }}>
          <p className="kpi-label">{t('projects.onHold')}</p>
          <p className="kpi-value">{projects.filter(p => p.status === "on-hold").length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
          <p className="kpi-label">{t('projects.completed')}</p>
          <p className="kpi-value">{projects.filter(p => p.status === "completed").length}</p>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
        {loading ? (
          <div className="col-span-full text-center py-12 text-[#8B95A5]">{t('projects.loading')}</div>
        ) : projects.length === 0 ? (
          <div className="col-span-full text-center py-12 text-[#8B95A5]">{t('projects.noProjects')}</div>
        ) : (
          projects.map((project) => (
            <div key={project.project_id} className="qms-card" data-testid={`project-${project.project_id}`}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-[3px] flex items-center justify-center"
                      style={{ backgroundColor: `${sectorColors[project.sector]}20` }}
                    >
                      <FolderKanban size={20} style={{ color: sectorColors[project.sector] }} />
                    </div>
                    <div>
                      <h3 className="font-['Syne'] font-semibold text-white">{project.name}</h3>
                      <span className="text-[#8B95A5] text-sm">{project.client_name}</span>
                    </div>
                  </div>
                  <span className={`badge ${getStatusBadge(project.status)} capitalize`}>
                    {project.status.replace("-", " ")}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#8B95A5]">{t('projects.sector')}</span>
                    <span className="text-white capitalize">{project.sector.replace("-", " ")}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#8B95A5]">{t('projects.projectId')}</span>
                    <span className="text-[#38BDF8] font-mono text-xs">{project.project_id}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#8B95A5]">{t('projects.auditors')}</span>
                    <span className="text-white">
                      {project.assigned_auditors?.length || 0} {t('projects.assigned')}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-[rgba(255,255,255,0.07)] flex gap-2">
                  {project.status === "active" && (
                    <button
                      onClick={() => handleStatusUpdate(project.project_id, "on-hold")}
                      className="text-xs text-[#FBBF24] hover:underline"
                    >
                      {t('projects.putOnHold')}
                    </button>
                  )}
                  {project.status === "on-hold" && (
                    <button
                      onClick={() => handleStatusUpdate(project.project_id, "active")}
                      className="text-xs text-[#4ADE80] hover:underline"
                    >
                      {t('projects.reactivate')}
                    </button>
                  )}
                  {project.status !== "completed" && (
                    <button
                      onClick={() => handleStatusUpdate(project.project_id, "completed")}
                      className="text-xs text-[#36A9E1] hover:underline ml-auto"
                    >
                      {t('projects.markComplete')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.07)]">
              <h3 className="font-['Syne'] text-lg font-semibold text-white">{t('projects.newProject')}</h3>
              <button onClick={() => setShowModal(false)} className="text-[#8B95A5] hover:text-white">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('projects.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="qms-input"
                  required
                  placeholder="e.g., GMP Implementation Phase 1"
                />
              </div>
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('projects.clientName')}</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="qms-input"
                  required
                  placeholder="e.g., PharmaCorp Ltd"
                />
              </div>
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">Sector</label>
                <select
                  value={formData.sector}
                  onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  className="qms-select"
                >
                  <option value="life-sciences">Life Sciences</option>
                  <option value="aerospace">Aerospace</option>
                  <option value="industrial">Industrial</option>
                  <option value="iot">IoT</option>
                </select>
              </div>
              {users.length > 0 && (
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('projects.assignAuditors')}</label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {users.map((user) => (
                      <label key={user.user_id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.assigned_auditors.includes(user.user_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                assigned_auditors: [...formData.assigned_auditors, user.user_id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                assigned_auditors: formData.assigned_auditors.filter(id => id !== user.user_id)
                              });
                            }
                          }}
                          className="accent-[#38BDF8]"
                        />
                        <span className="text-white text-sm">{user.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">{t('projects.createProject')}</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">{t('projects.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default ProjectsPage;
